// 엑셀 가져오기의 공통 모양 — 순수 함수들이 이 타입만 주고받는다.

import type { RowField } from "../editor/paste";
import { FilledItemInput } from "../editor/types";

/** 열 하나가 채울 곳. `amount_*` 는 담지 않고 원본 합계 대조에만 쓴다. */
export type ImportField =
  | Exclude<RowField, "simple_price">
  | "trade"
  | "material_amount"
  | "labor_amount"
  | "expense_unit_price"
  | "amount_total"
  | "amount_part";

/** 열 번호 → 채울 곳. null 이면 버리는 열. */
export type ColumnMap = (ImportField | null)[];

export const IMPORT_FIELD_LABEL: Record<ImportField, string> = {
  trade: "공종",
  space_code: "공간",
  scope: "부위",
  item: "작업내역",
  unit: "단위",
  qty: "수량",
  material_unit_price: "자재 단가",
  labor_unit_price: "노무 단가",
  cost_unit_price: "원가 단가",
  material_amount: "자재 금액",
  labor_amount: "노무 금액",
  expense_unit_price: "경비 단가(노무에 더함)",
  note: "메모",
  amount_total: "금액 합계(대조용)",
  amount_part: "자재·노무 금액(대조용)",
};

/** 드롭다운 순서. */
export const IMPORT_FIELDS: ImportField[] = [
  "trade",
  "space_code",
  "scope",
  "item",
  "unit",
  "qty",
  "material_unit_price",
  "labor_unit_price",
  "cost_unit_price",
  "material_amount",
  "labor_amount",
  "expense_unit_price",
  "note",
  "amount_total",
  "amount_part",
];

export interface HeaderGuess {
  /** 머리글 줄(격자 index). -1 이면 머리글 없음. */
  headerRow: number;
  /** 머리글이 두 줄(「자재비」 아래 「단가·금액」)이면 2. */
  headerRows: 1 | 2;
  /** 열마다 머리글 글자. 두 줄이면 합친 것. */
  labels: string[];
  columns: ColumnMap;
}

export interface ImportGroup {
  /** 엑셀에 적힌 공종 이름. 없으면 빈 문자열. */
  name: string;
  items: FilledItemInput[];
}

/** 미리보기에서 줄마다 붙일 판정. */
export type RowKind = "item" | "trade" | "total" | "blank";

export interface ImportParse {
  /** 격자 줄 index → 판정. 자료 시작 전·빈 줄은 null. */
  kinds: (RowKind | null)[];
  groups: ImportGroup[];
  rowCount: number;
  /** 못 읽은 칸 수 · 그런 칸이 있는 줄 수. */
  unclearCells: number;
  unclearRows: number;
  /** 소계·합계·부가세 같은 줄. */
  skippedTotals: number;
  /** 작업내역이 비어 버린 줄. */
  skippedBlank: number;
  /** 원본 합계 줄의 금액. 못 찾으면 null. */
  originalTotal: number | null;
  /** 대조한 원본 줄의 이름(「공급가액」·「합계」). */
  originalTotalLabel: string | null;
  /** 담지 않은 간접비 줄(공과잡비·이윤·일반관리비)의 금액 합. */
  indirectTotal: number;
  /** 경비 단가를 노무 단가에 더했다. */
  expenseMerged: boolean;
  /** 단가 열이 없어 금액÷수량으로 단가를 구한 줄 수. */
  pricedFromAmount: number;
  /** 공급가액·부가세 줄이 있다 — 원본 단가가 부가세 별도라는 단서. */
  vatSplitRows: boolean;
  /** 담을 줄로 다시 계산한 합계(자재+노무). 셀 수 없는 수면 null. */
  computedTotal: number | null;
  /** 줄 상한을 넘었다. */
  tooMany: boolean;
}

export const MAX_IMPORT_ROWS = 2000;
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
/** 머리글을 찾는 범위. */
export const HEADER_SCAN_ROWS = 20;
